'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { MeetingUserSelector, type MeetingUser } from '@/features/meeting-attendance/components/MeetingUserSelector';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from 'sonner';
import { Clock, Edit, FileText, Lock, MapPin, Plus, Trash2, Users } from 'lucide-react';

interface MeetingTemplate {
  id: string;
  name: string;
  title: string;
  description?: string;
  duration: number;
  location?: string;
  type: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  creator: {
    displayName: string;
    email: string;
  };
  templateAttendees: {
    user: MeetingUser;
  }[];
}

export default function MeetingAttendanceTemplatesPage() {
  const router = useRouter();
  const { isReady, user } = useAuthGuard();
  const { t } = useTranslation();

  const [templates, setTemplates] = useState<MeetingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MeetingTemplate | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<MeetingUser[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    duration: 60,
    location: '',
    type: 'OFFLINE',
    isPublic: false,
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await meetingAttendanceApi.listTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to fetch template list:', error);
      toast.error(t.meetingAttendance.templates.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [t.meetingAttendance.templates.loadFailed]);

  useEffect(() => {
    if (isReady) {
      fetchTemplates();
    }
  }, [isReady, fetchTemplates]);

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      description: '',
      duration: 60,
      location: '',
      type: 'OFFLINE',
      isPublic: false,
    });
    setSelectedUsers([]);
  };

  const handleCreateTemplate = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      if (editingTemplate) {
        await meetingAttendanceApi.updateTemplate(editingTemplate.id, {
          ...formData,
          attendeeIds: selectedUsers.map((user) => user.id),
        });
        toast.success(t.meetingAttendance.templates.updateSuccess);
      } else {
        await meetingAttendanceApi.createTemplate({
          ...formData,
          attendeeIds: selectedUsers.map((user) => user.id),
        });
        toast.success(t.meetingAttendance.templates.createSuccess);
      }

      setShowCreateForm(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error: any) {
      console.error('Failed to create template:', error);
      const message = error?.response?.data?.error || (
        editingTemplate ? t.meetingAttendance.templates.updateFailed : t.meetingAttendance.templates.createFailed
      );
      toast.error(message);
    }
  };

  const createMeetingFromTemplate = async (templateId: string) => {
    const startTime = window.prompt(t.meetingAttendance.templates.startTimePrompt);
    if (!startTime) return;

    const endTime = window.prompt(t.meetingAttendance.templates.endTimePrompt);
    if (!endTime) return;

    try {
      const data = await meetingAttendanceApi.createMeetingFromTemplate(templateId, {
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
      });

      toast.success(t.meetingAttendance.templates.createMeetingSuccess);
      router.push(`/meetingattendance/meetings/${data.meeting.id}`);
    } catch (error: any) {
      console.error('Failed to create meeting from template:', error);
      const message = error?.response?.data?.error || t.meetingAttendance.templates.createMeetingFailed;
      toast.error(message);
    }
  };

  const startEditing = (template: MeetingTemplate) => {
    setEditingTemplate(template);
    setShowCreateForm(true);
    setFormData({
      name: template.name,
      title: template.title,
      description: template.description || '',
      duration: template.duration,
      location: template.location || '',
      type: template.type,
      isPublic: template.isPublic,
    });
    setSelectedUsers(template.templateAttendees.map((attendee) => attendee.user));
  };

  const handleDeleteTemplate = async (template: MeetingTemplate) => {
    const confirmed = window.confirm(t.meetingAttendance.templates.deleteConfirm.replace('{name}', template.name));
    if (!confirmed) {
      return;
    }

    try {
      await meetingAttendanceApi.deleteTemplate(template.id);
      toast.success(t.meetingAttendance.templates.deleteSuccess);
      fetchTemplates();
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      const message = error?.response?.data?.error || t.meetingAttendance.templates.deleteFailed;
      toast.error(message);
    }
  };

  if (!isReady || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-600">{t.meetingAttendance.common.loading}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.meetingAttendance.templates.title}</h1>
          <p className="text-gray-600">{t.meetingAttendance.templates.subtitle}</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t.meetingAttendance.templates.createAction}
        </Button>
      </div>

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingTemplate
                ? t.meetingAttendance.templates.editTitle
                : t.meetingAttendance.templates.createTitle}
            </CardTitle>
            <CardDescription>
              {editingTemplate
                ? t.meetingAttendance.templates.editDescription
                : t.meetingAttendance.templates.createDescription}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t.meetingAttendance.templates.nameLabel}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                    placeholder={t.meetingAttendance.templates.namePlaceholder}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="title">{t.meetingAttendance.templates.meetingTitleLabel}</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(event) => setFormData({ ...formData, title: event.target.value })}
                    placeholder={t.meetingAttendance.templates.meetingTitlePlaceholder}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">{t.meetingAttendance.templates.descriptionLabel}</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(event) => setFormData({ ...formData, description: event.target.value })}
                  placeholder={t.meetingAttendance.templates.descriptionPlaceholder}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="duration">{t.meetingAttendance.templates.durationLabel}</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.duration}
                    onChange={(event) => setFormData({ ...formData, duration: parseInt(event.target.value, 10) || 60 })}
                    min="15"
                    step="15"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="type">{t.meetingAttendance.templates.typeLabel}</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={formData.type}
                    onChange={(event) => setFormData({ ...formData, type: event.target.value })}
                    aria-label={t.meetingAttendance.templates.typeLabel}
                  >
                    <option value="OFFLINE">{t.meetingAttendance.type.offline}</option>
                    <option value="ONLINE">{t.meetingAttendance.type.online}</option>
                    <option value="HYBRID">{t.meetingAttendance.type.hybrid}</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="location">{t.meetingAttendance.templates.locationLabel}</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(event) => setFormData({ ...formData, location: event.target.value })}
                    placeholder={t.meetingAttendance.templates.locationPlaceholder}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isPublic"
                  checked={formData.isPublic}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPublic: checked })}
                />
                <Label htmlFor="isPublic">{t.meetingAttendance.templates.publicLabel}</Label>
              </div>

              <div>
                <Label>{t.meetingAttendance.templates.attendeesLabel}</Label>
                <MeetingUserSelector selectedUsers={selectedUsers} onUsersChange={setSelectedUsers} />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingTemplate(null);
                    resetForm();
                  }}
                >
                  {t.meetingAttendance.common.back}
                </Button>
                <Button type="submit">
                  {editingTemplate
                    ? t.meetingAttendance.templates.updateAction
                    : t.meetingAttendance.templates.createAction}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => {
          const canManage = template.creator?.email && template.creator.email === (user as { email?: string })?.email;

          return (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>{template.name}</span>
                    {template.isPublic ? (
                      <span className="text-xs text-blue-600">{t.meetingAttendance.templates.publicTag}</span>
                    ) : (
                      <Lock className="h-4 w-4 text-gray-400" />
                    )}
                  </CardTitle>
                  <CardDescription>{template.title}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {template.description && (
                <p className="text-sm text-gray-600">{template.description}</p>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-600">
                  <Clock className="mr-2 h-4 w-4" />
                  {t.meetingAttendance.templates.durationValue.replace('{minutes}', String(template.duration))}
                </div>
                {template.location && (
                  <div className="flex items-center text-gray-600">
                    <MapPin className="mr-2 h-4 w-4" />
                    {template.location}
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <Users className="mr-2 h-4 w-4" />
                  {t.meetingAttendance.templates.attendeeCount.replace('{count}', String(template.templateAttendees.length))}
                </div>
              </div>

              <div className="text-xs text-gray-500">
                {t.meetingAttendance.templates.creatorLabel.replace('{name}', template.creator.displayName)}
              </div>

              <div className="flex justify-between pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => createMeetingFromTemplate(template.id)}
                >
                  {t.meetingAttendance.templates.createMeetingAction}
                </Button>
                {canManage && (
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEditing(template)}
                      title={t.meetingAttendance.templates.editAction}
                      aria-label={t.meetingAttendance.templates.editAction}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDeleteTemplate(template)}
                      title={t.meetingAttendance.templates.deleteAction}
                      aria-label={t.meetingAttendance.templates.deleteAction}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {templates.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t.meetingAttendance.templates.emptyTitle}</h3>
            <p className="text-gray-600 mb-4">{t.meetingAttendance.templates.emptyDescription}</p>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t.meetingAttendance.templates.createAction}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
